/**
 * ─────────────────────────────────────────────────────────────
 *  Get Maths Mastery — Combat Engine
 *  src/combat/engine.js
 *
 *  Self-contained, framework-agnostic.
 *  No React, no DOM, no App.js dependencies.
 *  All functions are pure unless noted.
 * ─────────────────────────────────────────────────────────────
 */

// ─────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────

export const TOPICS = ['addition', 'subtraction', 'multiplication', 'division', 'fractions'];

export const EFFECTS = {
  MULTIPLY:     'multiply',     // doubles attacker's damage this turn
  DIVIDE:       'divide',       // halves target's defence this turn
  PRIME_SHIELD: 'primeShield',  // blocks all damage if answer is a prime number
  NEGATIVE:     'negative',     // answer involves negative numbers (confusion debuff)
};

// Speed bonus brackets — how fast the answer was
const SPEED_BRACKETS = [
  { maxMs:  3000, multiplier: 2.0 },
  { maxMs:  6000, multiplier: 1.5 },
  { maxMs: 10000, multiplier: 1.2 },
  { maxMs: 20000, multiplier: 1.0 },
  { maxMs: Infinity, multiplier: 0.8 },
];

// ─────────────────────────────────────────────────────────────
//  INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────

function rand(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function isPrime(n) {
  if (n < 2) return false;
  for (let i = 2; i <= Math.sqrt(n); i++) if (n % i === 0) return false;
  return true;
}

function speedMultiplier(timeTakenMs) {
  return (SPEED_BRACKETS.find(b => timeTakenMs <= b.maxMs) ?? SPEED_BRACKETS.at(-1)).multiplier;
}

// ─────────────────────────────────────────────────────────────
//  QUESTION GENERATORS  keyed by [yearGroup][topic]
//  Each returns { question, answer, difficulty }  difficulty: 1–3
// ─────────────────────────────────────────────────────────────

const GENERATORS = {
  1: {
    addition:       () => { const a = rand(1,9),  b = rand(1,10-a);  return { question:`${a} + ${b} = ?`, answer:a+b,       difficulty:1 }; },
    subtraction:    () => { const a = rand(2,10), b = rand(1,a);     return { question:`${a} - ${b} = ?`, answer:a-b,       difficulty:1 }; },
    multiplication: () => { const a = rand(1,5),  b = rand(1,5);     return { question:`${a} × ${b} = ?`, answer:a*b,       difficulty:1 }; },
    division:       () => { const b = rand(1,5),  ans = rand(1,5);   return { question:`${b*ans} ÷ ${b} = ?`, answer:ans,   difficulty:1 }; },
    fractions:      () => { const a = rand(2,10); return { question:`½ of ${a*2} = ?`, answer:a, difficulty:1 }; },
  },
  2: {
    addition:       () => { const a = rand(10,50), b = rand(1,50);   return { question:`${a} + ${b} = ?`, answer:a+b,       difficulty:1 }; },
    subtraction:    () => { const a = rand(10,99), b = rand(1,a);    return { question:`${a} - ${b} = ?`, answer:a-b,       difficulty:2 }; },
    multiplication: () => { const b = [2,5,10][rand(0,2)], a=rand(1,12); return { question:`${a} × ${b} = ?`, answer:a*b,  difficulty:2 }; },
    division:       () => { const b = [2,5,10][rand(0,2)], ans=rand(1,12); return { question:`${b*ans} ÷ ${b} = ?`, answer:ans, difficulty:2 }; },
    fractions:      () => { const a = rand(1,10); return { question:`¼ of ${a*4} = ?`, answer:a, difficulty:2 }; },
  },
  3: {
    addition:       () => { const a = rand(100,500), b = rand(10,499); return { question:`${a} + ${b} = ?`, answer:a+b,     difficulty:2 }; },
    subtraction:    () => { const a = rand(100,999), b = rand(10,a);   return { question:`${a} - ${b} = ?`, answer:a-b,     difficulty:2 }; },
    multiplication: () => { const b = [3,4,8][rand(0,2)], a=rand(1,12); return { question:`${a} × ${b} = ?`, answer:a*b,   difficulty:2 }; },
    division:       () => { const b = [3,4,8][rand(0,2)], ans=rand(1,12); return { question:`${b*ans} ÷ ${b} = ?`, answer:ans, difficulty:2 }; },
    fractions:      () => { const d=[2,4][rand(0,1)], ans=rand(1,8), a=ans*d; return { question:`${d===2?'½':'¼'} of ${a} = ?`, answer:ans, difficulty:2 }; },
  },
  4: {
    addition:       () => { const a = rand(100,5000), b = rand(100,5000); return { question:`${a} + ${b} = ?`, answer:a+b,  difficulty:2 }; },
    subtraction:    () => { const a = rand(200,999),  b = rand(100,a);    return { question:`${a} - ${b} = ?`, answer:a-b,  difficulty:2 }; },
    multiplication: () => { const a = rand(1,12), b = rand(1,12);  return { question:`${a} × ${b} = ?`, answer:a*b,         difficulty:2 }; },
    division:       () => { const b = rand(2,12), ans = rand(1,12); return { question:`${b*ans} ÷ ${b} = ?`, answer:ans,    difficulty:3 }; },
    fractions:      () => { const d=rand(2,10), n=rand(1,d-1), m=rand(2,4); return { question:`${n}/${d} = ?/${d*m}`, answer:n*m, difficulty:3 }; },
  },
  5: {
    addition:       () => { const a=rand(10,990)/10, b=rand(10,990)/10; return { question:`${a} + ${b} = ?`, answer:Math.round((a+b)*10)/10, difficulty:2 }; },
    subtraction:    () => { const a=rand(110,990)/10, b=rand(10,Math.floor(a*10)-10)/10; return { question:`${a} - ${b} = ?`, answer:Math.round((a-b)*10)/10, difficulty:3 }; },
    multiplication: () => { const a = rand(12,50), b = rand(2,12); return { question:`${a} × ${b} = ?`, answer:a*b, difficulty:3 }; },
    division:       () => { const b = rand(3,12), ans = rand(10,50); return { question:`${b*ans} ÷ ${b} = ?`, answer:ans, difficulty:3 }; },
    fractions:      () => { const d=[3,4,5,8,10][rand(0,4)], n=rand(1,d-1), w=rand(2,20)*d; return { question:`${n}/${d} of ${w} = ?`, answer:Math.round(w*n/d), difficulty:3 }; },
  },
  6: {
    addition:       () => { const a=rand(-20,20), b=rand(-20,20); return { question:`${a} + (${b}) = ?`, answer:a+b, difficulty:3 }; },
    subtraction:    () => { const a=rand(100,999), b=rand(100,a);  return { question:`${a} - ${b} = ?`, answer:a-b,  difficulty:2 }; },
    multiplication: () => { const a = rand(100,999), b = rand(2,12); return { question:`${a} × ${b} = ?`, answer:a*b, difficulty:3 }; },
    division:       () => { const b = rand(4,12), ans = rand(10,120); return { question:`${b*ans} ÷ ${b} = ?`, answer:ans, difficulty:3 }; },
    fractions:      () => { const pcts=[10,20,25,50,75], p=pcts[rand(0,4)], w=rand(2,20)*(p===75||p===25?4:p===20?5:10); return { question:`${p}% of ${w} = ?`, answer:Math.round(w*p/100), difficulty:3 }; },
  },
};

// ─────────────────────────────────────────────────────────────
//  PUBLIC API
// ─────────────────────────────────────────────────────────────

/**
 * generateQuestion(yearGroup, topic)
 * @param {number} yearGroup  1–6
 * @param {string} topic      one of TOPICS
 * @returns {{ question: string, answer: number, difficulty: 1|2|3 }}
 */
export function generateQuestion(yearGroup, topic) {
  const yg  = Math.max(1, Math.min(6, Math.round(yearGroup)));
  const top = TOPICS.includes(topic) ? topic : TOPICS[rand(0, TOPICS.length - 1)];
  return GENERATORS[yg][top]();
}

// ─────────────────────────────────────────────────────────────

/**
 * calculateDamage(attackStat, isCorrect, timeTakenMs)
 * Fast correct answers deal more damage.
 * Wrong answers deal 0.
 * @returns {number}
 */
export function calculateDamage(attackStat, isCorrect, timeTakenMs) {
  if (!isCorrect) return 0;
  return Math.floor(attackStat * 2 * speedMultiplier(timeTakenMs));
}

// ─────────────────────────────────────────────────────────────

/**
 * calculateBlock(defenceStat, isCorrect)
 * Correct defence answer = full block value.
 * Wrong answer = 30% of block value.
 * @returns {number}
 */
export function calculateBlock(defenceStat, isCorrect) {
  const base = Math.floor(defenceStat * 0.8);
  return isCorrect ? base : Math.floor(base * 0.3);
}

// ─────────────────────────────────────────────────────────────

/**
 * applyStatusEffect(character, effect)
 * Does NOT mutate the original object.
 * @param {object} character
 * @param {string} effect  one of EFFECTS.*
 * @returns {object}  updated character
 */
export function applyStatusEffect(character, effect) {
  const existing = (character.activeEffects ?? []).filter(e => e !== effect);
  return { ...character, activeEffects: [...existing, effect] };
}

/**
 * removeStatusEffect(character, effect)
 * @returns {object}  updated character
 */
export function removeStatusEffect(character, effect) {
  return {
    ...character,
    activeEffects: (character.activeEffects ?? []).filter(e => e !== effect),
  };
}

/**
 * hasEffect(character, effect)
 * @returns {boolean}
 */
export function hasEffect(character, effect) {
  return (character.activeEffects ?? []).includes(effect);
}

// ─────────────────────────────────────────────────────────────

/**
 * checkWinCondition(playerHp, enemyHp)
 * @returns {'win'|'lose'|'ongoing'}
 */
export function checkWinCondition(playerHp, enemyHp) {
  if (enemyHp  <= 0) return 'win';
  if (playerHp <= 0) return 'lose';
  return 'ongoing';
}

// ─────────────────────────────────────────────────────────────

/**
 * awardXP(questionsCorrect, difficulty, timeTakenMs)
 * @param {number} questionsCorrect
 * @param {number} difficulty  1–3
 * @param {number} timeTakenMs  total time across all questions
 * @returns {number}  XP earned
 */
export function awardXP(questionsCorrect, difficulty, timeTakenMs) {
  const basePerQ  = difficulty === 3 ? 30 : difficulty === 2 ? 18 : 10;
  const base      = questionsCorrect * basePerQ;
  const avgMs     = questionsCorrect > 0 ? timeTakenMs / questionsCorrect : 20000;
  const speedBonus = avgMs <= 3000 ? 0.5 : avgMs <= 6000 ? 0.3 : avgMs <= 10000 ? 0.15 : 0;
  return Math.floor(base * (1 + speedBonus));
}

// ─────────────────────────────────────────────────────────────
//  DEFAULT CHARACTERS  (matches the 5 in App.js)
// ─────────────────────────────────────────────────────────────

export const DEFAULT_CHARACTERS = [
  {
    id:        'mage',
    name:      'Mage',
    hp:        80,
    maxHp:     80,
    attack:    28,
    defence:   8,
    speed:     22,
    topic:     'multiplication',
    color:     '#a855f7',
    ability: {
      name:        'Arcane Surge',
      description: 'Doubles attack damage for one turn.',
      effect:      EFFECTS.MULTIPLY,
    },
  },
  {
    id:        'knight',
    name:      'Knight',
    hp:        130,
    maxHp:     130,
    attack:    18,
    defence:   22,
    speed:     10,
    topic:     'addition',
    color:     '#f59e0b',
    ability: {
      name:        'Iron Guard',
      description: 'Raises a prime shield — blocks all damage if the answer is prime.',
      effect:      EFFECTS.PRIME_SHIELD,
    },
  },
  {
    id:        'archer',
    name:      'Archer',
    hp:        95,
    maxHp:     95,
    attack:    22,
    defence:   12,
    speed:     26,
    topic:     'subtraction',
    color:     '#22c55e',
    ability: {
      name:        'Piercing Shot',
      description: "Halves the enemy's defence for one turn.",
      effect:      EFFECTS.DIVIDE,
    },
  },
  {
    id:        'scholar',
    name:      'Scholar',
    hp:        90,
    maxHp:     90,
    attack:    20,
    defence:   16,
    speed:     16,
    topic:     'fractions',
    color:     '#3b82f6',
    ability: {
      name:        'Mind Fog',
      description: 'Inflicts negative confusion on the enemy — their next question involves negatives.',
      effect:      EFFECTS.NEGATIVE,
    },
  },
  {
    id:        'bard',
    name:      'Bard',
    hp:        100,
    maxHp:     100,
    attack:    20,
    defence:   14,
    speed:     20,
    topic:     'division',
    color:     '#ef4444',
    ability: {
      name:        'Battle Hymn',
      description: 'Inspires the whole party — everyone gets a speed bonus next turn.',
      effect:      EFFECTS.MULTIPLY,
    },
  },
];
